import { useEffect, useMemo, useState } from "react";
import { Truck, MapPin, Clock, Printer, ChevronDown, ChevronUp, CheckSquare, Square, Download, Paperclip, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { printShipmentLabel, printMultipleLabels } from "./ShipmentLabel";
import { printMonthlySummary } from "./MonthlySummaryPrint";
import { formatDateIT, formatTimelineIT } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMap = {
  in_attesa: { label: "Presa in Carico", color: "bg-zinc-400" },
  ritirato: { label: "Ritirato", color: "bg-blue-500" },
  in_transito: { label: "In Transito", color: "bg-[#FF5200]" },
  in_consegna: { label: "In Consegna", color: "bg-amber-500" },
  consegnato: { label: "Consegnato", color: "bg-green-500" },
  cancellation_requested: { label: "Annullamento Richiesto", color: "bg-red-500" },
  annullato: { label: "Annullato", color: "bg-red-700" },
};

const getMonthKey = (value) => new Date(value).toISOString().slice(0, 7);
const getMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

const getReadyBadgeLabel = (item) => {
  if (item.goods_ready_status !== "pronta") return null;
  return item.goods_ready_time ? `Pronta alle ${item.goods_ready_time}` : "Pronta";
};

const getDdtBadgeLabel = (item) => {
  const count = item.ddt_files?.length || 0;
  if (!count) return null;
  return count > 1 ? `DDT PDF (${count})` : "DDT PDF";
};

const downloadPdf = (file) => {
  const binary = atob(file.data_base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const blob = new Blob([bytes], { type: file.content_type || "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const downloadPodPdf = async (tracking) => {
  if (!tracking) return;
  try {
    const url = `${API}/tracking/${tracking}/pod`;
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `POD_${tracking}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    toast.success("POD scaricato");
  } catch {
    toast.error("Errore download POD");
  }
};

export default function CustomerShipments({ items = [], token, mode = "spedizione", user = null, onRefresh = () => {} }) {
  const shipments = items;
  const isPickup = mode === "ritiro";
  const emptyLabel = isPickup ? "Nessun ritiro trovato." : "Nessuna spedizione trovata.";
  const emptyHint = isPickup ? 'Crea il tuo primo ritiro dalla tab "Nuovo Ritiro".' : 'Crea la tua prima spedizione dalla tab "Nuova Spedizione".';
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [savingTracking, setSavingTracking] = useState("");
  const [goodsForm, setGoodsForm] = useState({});
  const [cancellingTracking, setCancellingTracking] = useState("");

  const months = useMemo(() => {
    return [...new Set(shipments.map((item) => getMonthKey(item.created_at)))].sort((a, b) => b.localeCompare(a));
  }, [shipments]);

  useEffect(() => {
    if (!months.length) {
      setSelectedMonth("");
      return;
    }
    if (!selectedMonth || !months.includes(selectedMonth)) {
      setSelectedMonth(months[0]);
    }
  }, [months, selectedMonth]);

  const monthlyItems = useMemo(() => {
    if (!selectedMonth) return shipments;
    return shipments.filter((item) => getMonthKey(item.created_at) === selectedMonth);
  }, [selectedMonth, shipments]);

  useEffect(() => {
    setSelected(new Set());
    setExpanded(null);
  }, [selectedMonth]);

  useEffect(() => {
    const next = {};
    shipments.forEach((item) => {
      next[item.tracking_number] = {
        goods_ready_status: item.goods_ready_status || "non_pronta",
        goods_ready_time: item.goods_ready_time || "",
      };
    });
    setGoodsForm(next);
  }, [shipments]);

  const toggleSelect = (tn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tn)) next.delete(tn);
      else next.add(tn);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === monthlyItems.length) setSelected(new Set());
    else setSelected(new Set(monthlyItems.map((s) => s.tracking_number)));
  };

  const handlePrintSelected = () => {
    const toPrint = monthlyItems.filter((s) => selected.has(s.tracking_number));
    if (toPrint.length === 0) return;
    printMultipleLabels(toPrint);
  };

  const handlePrintMonthlySummary = () => {
    if (!selectedMonth || monthlyItems.length === 0) return;
    printMonthlySummary({ items: monthlyItems, mode, monthKey: selectedMonth, user });
  };

  const updateGoodsReady = async (tracking, overrideStatus = null) => {
    const payload = goodsForm[tracking] || { goods_ready_status: "non_pronta", goods_ready_time: "" };
    setSavingTracking(tracking);
    try {
      await axios.put(`${API}/${isPickup ? "pickups" : "shipments"}/${tracking}/goods-ready`, {
        goods_ready_status: overrideStatus || payload.goods_ready_status,
        goods_ready_time: payload.goods_ready_time,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await onRefresh();
    } finally {
      setSavingTracking("");
    }
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadDdtFiles = async (tracking, files) => {
    const pdfs = Array.from(files || []);
    if (!pdfs.length) return;
    if (pdfs.some((file) => file.type !== "application/pdf")) return;

    setSavingTracking(tracking);
    try {
      const encoded = await Promise.all(pdfs.map(async (file) => ({ filename: file.name, content_type: file.type, data_base64: await toBase64(file) })));
      await axios.post(`${API}/${isPickup ? "pickups" : "shipments"}/${tracking}/ddt`, { files: encoded }, { headers: { Authorization: `Bearer ${token}` } });
      await onRefresh();
    } finally {
      setSavingTracking("");
    }
  };

  const requestCancellation = async (tracking) => {
    if (!window.confirm(`Confermi la richiesta di annullamento per ${tracking}?`)) return;
    setCancellingTracking(tracking);
    try {
      await axios.put(`${API}/${isPickup ? "pickups" : "shipments"}/${tracking}/request-cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Richiesta di annullamento inviata");
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella richiesta di annullamento");
    } finally {
      setCancellingTracking("");
    }
  };

  if (shipments.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-400 font-body" data-testid="no-shipments">
        <Truck size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg">{emptyLabel}</p>
        <p className="text-sm mt-1">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div data-testid="shipments-list">
      {/* Bulk actions bar */}
      <div className="flex flex-col gap-4 mb-4 pb-4 border-b-2 border-zinc-100">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-500 font-heading" htmlFor={`month-select-${mode}`}>
              Mese resoconto
            </label>
            <select
              id={`month-select-${mode}`}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-10 px-3 border-2 border-zinc-200 rounded-sm text-sm font-bold text-zinc-700 bg-white"
              data-testid="monthly-summary-month-select"
            >
              {months.map((monthKey) => (
                <option key={monthKey} value={monthKey}>{getMonthLabel(monthKey)}</option>
              ))}
            </select>
            <button
              onClick={handlePrintMonthlySummary}
              className="inline-flex items-center gap-2 bg-[#09090B] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-800 transition-colors font-heading"
              data-testid="print-monthly-summary-btn"
            >
              <Printer size={16} /> Stampa resoconto mensile
            </button>
          </div>
          <div className="text-sm text-zinc-500" data-testid="monthly-summary-count">
            {monthlyItems.length} {monthlyItems.length === 1 ? (isPickup ? "ritiro" : "spedizione") : (isPickup ? "ritiri" : "spedizioni")} in {selectedMonth ? getMonthLabel(selectedMonth) : "mese selezionato"}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={toggleAll}
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-zinc-500 hover:text-[#FF5200] transition-colors font-heading"
            data-testid="select-all-btn"
          >
            {selected.size === monthlyItems.length && monthlyItems.length > 0 ? <CheckSquare size={18} className="text-[#FF5200]" /> : <Square size={18} />}
            {selected.size === monthlyItems.length && monthlyItems.length > 0 ? "Deseleziona tutto" : "Seleziona tutto"}
          </button>
          {selected.size > 0 && (
            <button
              onClick={handlePrintSelected}
              className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-5 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors font-heading"
              data-testid="print-selected-btn"
            >
              <Printer size={16} />
              Stampa etichette ({selected.size})
            </button>
          )}
        </div>
      </div>

      {/* Shipments */}
      <div className="space-y-4">
        {monthlyItems.map((s) => {
          const st = statusMap[s.status] || { label: s.status, color: "bg-zinc-400" };
          const isExpanded = expanded === s.tracking_number;
          const isSelected = selected.has(s.tracking_number);
          return (
            <div
              key={s.tracking_number}
              className={`border-2 transition-colors duration-200 ${isSelected ? "border-[#FF5200] bg-[#FF5200]/[0.02]" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid={`shipment-${s.tracking_number}`}
            >
              {/* Shipment Header */}
              <div className="p-5 flex items-center gap-4">
                {/* Checkbox */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelect(s.tracking_number); }}
                  className="flex-shrink-0 text-zinc-300 hover:text-[#FF5200] transition-colors"
                  data-testid={`select-${s.tracking_number}`}
                >
                  {isSelected ? <CheckSquare size={20} className="text-[#FF5200]" /> : <Square size={20} />}
                </button>

                {/* Main content - clickable to expand */}
                <div
                  className="flex-1 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-3"
                  onClick={() => setExpanded(isExpanded ? null : s.tracking_number)}
                >
                  <div className="flex items-center gap-4">
                    <Truck size={24} className="text-[#FF5200] flex-shrink-0" />
                    <div>
                      <p className="font-bold font-heading text-[#09090B]">
                        {s.tracking_number}
                        <span className="ml-2 font-body text-sm font-normal text-zinc-500">
                          {isPickup ? s.origin_details?.name || s.origin : s.dest_details?.name || s.destination}
                        </span>
                      </p>
                      <p className="text-sm text-zinc-500 font-body">
                        {s.origin} &rarr; {s.destination}
                        {s.colli && s.colli.length > 0 && <span className="ml-2 text-zinc-400">| {s.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)} {s.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0) === 1 ? "collo" : "colli"}</span>}
                      </p>
                      {(s.scheduled_date || s.created_at) && (
                        <p className="text-xs font-bold text-[#FF5200] mt-0.5" data-testid={`date-${s.tracking_number}`}>
                          {(() => { const d = s.scheduled_date || (s.created_at ? s.created_at.slice(0, 10) : ""); return d ? d.split("-").reverse().join("-") : ""; })()}
                        </p>
                      )}
                      {s.note && (
                        <p className="text-xs text-zinc-400 mt-0.5 italic" data-testid={`note-${s.tracking_number}`}>
                          {s.note}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {getReadyBadgeLabel(s) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm" data-testid={`customer-ready-badge-${s.tracking_number}`}>{getReadyBadgeLabel(s)}</span>}
                        {getDdtBadgeLabel(s) && <button type="button" onClick={(e) => { e.stopPropagation(); downloadPdf(s.ddt_files[0]); }} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200" data-testid={`customer-ddt-badge-${s.tracking_number}`}><Paperclip size={12} /> {getDdtBadgeLabel(s)}</button>}
                        {s.insurance && <span className="px-2 py-1 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-sm" data-testid={`customer-insurance-badge-${s.tracking_number}`}>Assicurata {s.insurance_value ? `€${s.insurance_value}` : ""}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-zinc-500 font-body">{s.service_type}</span>
                    <span className={`px-3 py-1 text-white text-xs font-bold uppercase tracking-wide rounded-sm ${st.color}`}>
                      {st.label}
                    </span>
                    {s.status === "in_attesa" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); requestCancellation(s.tracking_number); }}
                        disabled={cancellingTracking === s.tracking_number}
                        className="p-2 text-zinc-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Richiedi annullamento"
                        data-testid={`request-cancel-${s.tracking_number}`}
                      >
                        <XCircle size={18} />
                      </button>
                    )}
                    {s.status === "consegnato" && s.pod && (
                      <a
                        href={`${API}/tracking/${s.tracking_number}/pod`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-zinc-400 hover:text-[#16a34a] transition-colors"
                        title="Scarica POD (Prova di consegna)"
                        data-testid={`download-pod-${s.tracking_number}`}
                      >
                        <Download size={18} />
                      </a>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); printShipmentLabel(s); }}
                      className="p-2 text-zinc-400 hover:text-[#FF5200] transition-colors"
                      title="Stampa etichetta"
                      data-testid={`print-label-${s.tracking_number}`}
                    >
                      <Printer size={18} />
                    </button>
                    {isExpanded ? <ChevronUp size={18} className="text-zinc-400" /> : <ChevronDown size={18} className="text-zinc-400" />}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t-2 border-zinc-100 p-5 bg-[#FAFAFA]" data-testid={`shipment-details-${s.tracking_number}`}>
                  {/* Address details */}
                  {(s.origin_details || s.dest_details) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {s.origin_details && (
                        <div>
                          <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-2 font-heading">Mittente</p>
                          <p className="font-bold text-[#09090B]">{s.origin_details.name}</p>
                          {s.origin_details.company && <p className="text-sm text-zinc-500">{s.origin_details.company}</p>}
                          <p className="text-sm text-zinc-500">{s.origin_details.address}</p>
                          <p className="text-sm text-zinc-500">{s.origin_details.cap} {s.origin_details.city} ({s.origin_details.province})</p>
                          {s.origin_details.phone && <p className="text-sm text-zinc-500">Tel: {s.origin_details.phone}</p>}
                        </div>
                      )}
                      {s.dest_details && (
                        <div>
                          <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-2 font-heading">Destinatario</p>
                          <p className="font-bold text-[#09090B]">{s.dest_details.name}</p>
                          {s.dest_details.company && <p className="text-sm text-zinc-500">{s.dest_details.company}</p>}
                          <p className="text-sm text-zinc-500">{s.dest_details.address}</p>
                          <p className="text-sm text-zinc-500">{s.dest_details.cap} {s.dest_details.city} ({s.dest_details.province})</p>
                          {s.dest_details.phone && <p className="text-sm text-zinc-500">Tel: {s.dest_details.phone}</p>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Colli list */}
                  {s.colli && s.colli.length > 0 && (
                    <div className="mb-6">
                      <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-3 font-heading">Colli ({s.colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)})</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {s.colli.map((c, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-white border border-zinc-200">
                            <span className="w-7 h-7 bg-[#FF5200] text-white text-xs font-bold flex items-center justify-center rounded-sm flex-shrink-0">{c.quantita > 1 ? `x${c.quantita}` : i + 1}</span>
                            <div className="text-sm font-body">
                              <p className="font-bold text-[#09090B]">{c.tipo === "pallet" ? "Pallet" : "Collo"}{c.quantita > 1 ? ` (x${c.quantita})` : ""}</p>
                              <p className="text-xs text-zinc-500">
                                {c.peso && `${c.peso} kg`}{c.peso && c.dimensioni && " - "}{c.dimensioni && c.dimensioni}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy fields for older shipments */}
                  {!s.colli && (s.weight || s.dimensions || s.numero_colli) && (
                    <div className="flex flex-wrap gap-6 mb-6 text-sm text-zinc-600 font-body">
                      {s.weight && <p>Peso: <strong>{s.weight} kg</strong></p>}
                      {s.dimensions && <p>Dimensioni: <strong>{s.dimensions}</strong></p>}
                      {s.numero_colli && <p>Colli: <strong>{s.numero_colli} {s.tipo_imballo || ""}</strong></p>}
                    </div>
                  )}

                  {/* Descrizione merce + Note */}
                  {(s.descrizione_merce || s.note) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {s.descrizione_merce && (
                        <div className="bg-white border border-zinc-200 p-4" data-testid={`customer-merce-${s.tracking_number}`}>
                          <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-2 font-heading">Descrizione Merce</p>
                          <p className="text-sm text-[#09090B] whitespace-pre-wrap">{s.descrizione_merce}</p>
                        </div>
                      )}
                      {s.note && (
                        <div className="bg-amber-50 border border-amber-200 p-4" data-testid={`customer-note-${s.tracking_number}`}>
                          <p className="text-xs font-bold tracking-widest uppercase text-amber-600 mb-2 font-heading">Note</p>
                          <p className="text-sm text-[#09090B] whitespace-pre-wrap">{s.note}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* POD download (after delivery) */}
                  {s.status === "consegnato" && s.pod && (
                    <div className="bg-green-50 border border-green-200 p-4 mb-6" data-testid={`customer-pod-${s.tracking_number}`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <p className="text-xs font-bold tracking-widest uppercase text-green-700 mb-1 font-heading">Prova di Consegna (POD)</p>
                          <p className="text-sm text-[#09090B]">
                            {s.pod.signed_by && <><span className="font-bold">Firmato da:</span> {s.pod.signed_by} · </>}
                            {s.pod.timestamp && formatTimelineIT(s.pod.timestamp)}
                          </p>
                          {s.pod.note && <p className="text-xs text-green-700 mt-1">Note: {s.pod.note}</p>}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadPodPdf(s.tracking_number); }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-green-700"
                          data-testid={`customer-download-pod-${s.tracking_number}`}
                        >
                          <Download size={14} /> Scarica POD PDF
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white border border-zinc-200 p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 font-heading">Merce pronta</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button type="button"
                          onClick={() => { setGoodsForm((prev) => ({ ...prev, [s.tracking_number]: { goods_ready_status: "pronta", goods_ready_time: "" } })); updateGoodsReady(s.tracking_number, "pronta"); }}
                          className={`h-11 px-3 border-2 rounded-sm text-xs font-bold uppercase tracking-wide transition-colors ${s.goods_ready_status === "pronta" && !s.goods_ready_time ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                          data-testid={`mark-ready-${s.tracking_number}`}>
                          Merce pronta
                        </button>
                        <div className="flex items-stretch gap-0">
                          <span className={`flex items-center flex-shrink-0 px-3 border-2 border-r-0 rounded-l-sm text-xs font-bold uppercase tracking-wide ${(goodsForm[s.tracking_number]?.goods_ready_time || (s.goods_ready_status === "pronta" && s.goods_ready_time)) ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600"}`}>
                            Pronta alle
                          </span>
                          <input type="time" value={goodsForm[s.tracking_number]?.goods_ready_time ?? s.goods_ready_time ?? ""}
                            onChange={(e) => setGoodsForm((prev) => ({ ...prev, [s.tracking_number]: { goods_ready_status: "pronta", goods_ready_time: e.target.value } }))}
                            className={`flex-1 h-11 px-2 border-2 rounded-r-sm text-sm outline-none ${goodsForm[s.tracking_number]?.goods_ready_time ? "border-[#FF5200]" : "border-zinc-200"} focus:border-[#FF5200]`}
                            data-testid={`goods-time-${s.tracking_number}`} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-3 text-sm">
                        <span className="text-zinc-500">Stato attuale: <strong>{getReadyBadgeLabel(s) || "Non pronta"}</strong></span>
                        <button type="button" onClick={() => updateGoodsReady(s.tracking_number)} className="px-3 py-2 border-2 border-zinc-200 text-xs font-bold uppercase rounded-sm" data-testid={`save-goods-ready-${s.tracking_number}`}>
                          Salva stato
                        </button>
                      </div>
                    </div>

                    <div className="bg-white border border-zinc-200 p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 font-heading">DDT PDF</p>
                        <label className="inline-flex items-center gap-2 px-3 py-2 bg-[#09090B] text-white text-[11px] font-bold uppercase rounded-sm cursor-pointer" data-testid={`upload-ddt-${s.tracking_number}`}>
                          <Paperclip size={14} /> Carica PDF
                          <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => uploadDdtFiles(s.tracking_number, e.target.files)} data-testid={`upload-ddt-input-${s.tracking_number}`} />
                        </label>
                      </div>
                      {s.ddt_files?.length ? (
                        <div className="space-y-2">
                          {s.ddt_files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between gap-3 border border-zinc-200 bg-[#FAFAFA] p-3" data-testid={`ddt-download-${file.id}`}>
                              <div>
                                <p className="text-sm font-bold text-[#09090B]">{file.filename}</p>
                                <p className="text-xs text-zinc-500">Caricato il {formatDateIT(file.uploaded_at)}</p>
                              </div>
                              <button type="button" onClick={() => downloadPdf(file)} className="p-2 text-zinc-500 hover:text-[#FF5200]" data-testid={`download-ddt-${file.id}`}>
                                <Download size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-500">Nessun DDT caricato.</p>
                      )}
                    </div>
                  </div>

                  {/* Timeline */}
                  {s.status_history && s.status_history.length > 0 && (
                    <div>
                      <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-3 font-heading">Cronologia</p>
                      <div className="space-y-0">
                        {[...s.status_history].reverse().map((event, i) => {
                          const est = statusMap[event.status] || { label: event.status, color: "bg-zinc-400" };
                          return (
                            <div key={i} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className={`w-3 h-3 rounded-full ${est.color} flex-shrink-0 ${i === 0 ? "ring-2 ring-opacity-30 ring-current" : ""}`} />
                                {i < s.status_history.length - 1 && <div className="w-0.5 h-full min-h-[40px] bg-zinc-200" />}
                              </div>
                              <div className="pb-4">
                                <p className="font-bold text-sm text-[#09090B]">{est.label}</p>
                                <p className="text-sm text-zinc-500">{event.note}</p>
                                <div className="flex gap-3 mt-1 text-xs text-zinc-400">
                                  {event.location && <span className="flex items-center gap-1"><MapPin size={10} /> {event.location}</span>}
                                  <span className="flex items-center gap-1">
                                    <Clock size={10} />
                                    {formatTimelineIT(event.timestamp)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
